import { Controller } from '@hotwired/stimulus'

// LRPO-1628 — Companies criterion card.
//
// ★ NO NAMED INPUT UNTIL COMMITTED. The builder autosaves every 30 seconds, so a
// half-typed company name must be impossible to persist. The guard is structural
// (the input does not exist), not a validation.
//
// ★ A company name is ATTENDEE-SUPPLIED TEXT. Every node here is built with
// createElement/textContent/setAttribute — it must never reach innerHTML, not
// even inside an attribute.

const DEBOUNCE_MS = 250

// LRPO-5655 (QA return) — how many EXTRA pages one search may pull on its own when
// every row it gets back is filtered out as already-added. Each hop is a LIKE query
// over attendees, so this is the bound that keeps a single keystroke from walking
// the entire result set.
const MAX_AUTO_ADVANCE = 5

const MAX_VALUE_LENGTH = 255

// LRPO-5822 — the count is correct but does not say WHOSE leads it counts, and
// QA reported the correct number as a bug twice. The visible sentence cannot
// change: LRPO-5785 set it on 2026-08-06 under the rule MATCH MEANS ASSIGNED —
// a lead is in a territory because rep matching assigned it AT SCAN TIME, and
// editing a territory is not retroactive — so the number must never read as a
// statement about this territory. The explanation goes here instead.
//
// ⚠ This text is held to the same rule as the sentence. It must never say the
// leads are "in this territory" or "assigned to this territory"; a spec asserts
// that. The ERB partial _territory_companies.html.erb repeats this string for
// server-rendered chips — change it there in the same commit.
const COUNT_TOOLTIP =
  'Your scanned leads at this company. Leads are matched when they are scanned, ' +
  'so changing this territory does not move leads you already have.'

// Mirrors utf8mb4_0900_ai_ci: case- and accent-insensitive. The DOM keeps every
// name verbatim; only COMPARISONS go through here.
const norm = value => String(value == null ? '' : value)
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')

export default class extends Controller {
  static targets = ['input', 'results', 'chips', 'hiddenFields', 'count', 'unset']
  static values  = { url: String, group: Number, readonly: Boolean }

  connect () {
    // Seed this.committed from server-rendered wrappers so the count is correct
    // and the duplicate guard recognises already-saved criteria on mount.
    if (this.hasHiddenFieldsTarget) {
      this.committed = Array.from(this.hiddenFieldsTarget.children)
        .filter(wrap => {
          const destroyInput = wrap.querySelector('input[name$="[_destroy]"]')
          return destroyInput ? destroyInput.value !== 'true' : true
        })
        .map(wrap => wrap.dataset.name)
        .filter(Boolean)
    } else if (this.hasChipsTarget) {
      // A read-only card renders chips but no hidden inputs. Without this
      // fallback the server-rendered count is clobbered to zero and the
      // duplicate guard is blind.
      this.committed = Array.from(this.chipsTarget.children)
        .map(chip => chip.dataset.name)
        .filter(Boolean)
    }
    this.committed = this.committed || []
    this.syncCount()
    this.activeIndex = -1
    this.debounceTimer = null
    this.abortController = null
    this._onAutosaved = event => this.reconcileIds(event.detail)
    document.addEventListener('territory:autosaved', this._onAutosaved)

    // LRPO-5831 — mark every wrapper going out in THIS request. Once a POST has
    // carried a company, the server may have created it, so removing that chip
    // has to leave a wrapper behind to receive the id and destroy the row. A
    // company that has never been posted cannot exist, so it is still removed
    // outright.
    this._onAutosaving = () => {
      if (!this.hasHiddenFieldsTarget) return
      Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
        wrapper.dataset.posted = 'true'
      })
    }
    document.addEventListener('territory:autosaving', this._onAutosaving)
  }

  // ---- lifecycle ----------------------------------------------------------

  disconnect () {
    clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
    document.removeEventListener('territory:autosaved', this._onAutosaved)
    document.removeEventListener('territory:autosaving', this._onAutosaving)
  }

  // ---- typing -------------------------------------------------------------

  search () {
    const term = this.inputTarget.value.trim()
    clearTimeout(this.debounceTimer)

    if (term.length < 2) {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.clearResults()
      return
    }

    // One request per keystroke turns a fast typist into a burst of LIKE queries
    // over attendees. Wait for the typing to stop, then cancel whatever is still
    // in flight before starting the next one.
    this.debounceTimer = setTimeout(() => this.performSearch(term), DEBOUNCE_MS)
  }

  performSearch (term) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    // LRPO-5655 — reset paging for a fresh search.
    this.page = 1
    // ...and give the new term its own auto-advance budget, or a second hard
    // search in the same session would refuse to advance at all.
    this.autoAdvances = 0

    fetch(`${this.urlValue}?term=${encodeURIComponent(term)}&page=${this.page}`, {
      headers: { Accept: 'application/json' },
      signal: this.abortController.signal
    })
      .then(r => { if (!r.ok) throw new Error('search failed'); return r.json() })
      .then(body => {
        this.hasMore = !!body.has_more
        this.renderResults(body.data || [], term, false)
        this.autoAdvanceIfEmpty(term)
      })
      .catch(err => {
        // An abort is the expected outcome of typing another character. Only a
        // real failure should blank the list.
        if (err && err.name === 'AbortError') return
        this.clearResults()
      })
  }

  navigate (event) {
    if (!this.hasResultsTarget) return

    const options = this.resultsTarget.querySelectorAll('li:not([data-role="already-added"]):not([data-role="load-more"]):not([data-role="searching"])')

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1)
      this.applyActiveOption(options)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, -1)
      this.applyActiveOption(options)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this.activeIndex >= 0 && options[this.activeIndex]) {
        const opt = options[this.activeIndex]
        this.commit(opt.dataset.name, Number(opt.dataset.leadCount))
      } else {
        // LRPO-5822 — `null` means NOT KNOWN YET, and appendChip already honours
        // it by omitting both the count text and the zero warning. Committing 0
        // here (LRPO-5768/5799) was a guess, and when the debounce had not yet
        // answered it was usually a WRONG one: it told the user a company with
        // real leads had none. Ask the server instead.
        const typed = this.inputTarget.value
        const exactOpt = Array.from(options).find(opt => norm(opt.dataset.name) === norm(typed))

        if (exactOpt) {
          this.commit(typed, Number(exactOpt.dataset.leadCount))
        } else {
          this.commit(typed, null)
          this.lookupLeadCount(typed)
        }
      }
    } else if (event.key === 'Escape') {
      event.preventDefault()
      clearTimeout(this.debounceTimer)
      this.clearResults()
    }
  }

  applyActiveOption (options) {
    options.forEach(opt => opt.classList.remove('is-active'))
    if (this.activeIndex >= 0 && options[this.activeIndex]) {
      const active = options[this.activeIndex]
      active.classList.add('is-active')
      this.inputTarget.setAttribute('aria-activedescendant', active.id)
    } else {
      this.inputTarget.removeAttribute('aria-activedescendant')
    }
  }

  // ---- committing ---------------------------------------------------------

  commit (rawName, leadCount = null) {
    clearTimeout(this.debounceTimer)
    const name = String(rawName == null ? '' : rawName).trim()
    if (!name) return                                   // blank never commits
    if (name.length > MAX_VALUE_LENGTH) return           // too long for varchar(255)
    if (this.committedValues().some(v => norm(v) === norm(name))) return   // no duplicates (collation-aware)

    // Read-only: render the chip but emit zero inputs.
    if (this.readonlyValue) {
      this.committed.push(name)
      this.appendChip(name, leadCount)
      this.clearResults()
      if (this.hasInputTarget) this.inputTarget.value = ''
      this.syncCount()
      return
    }

    // Revive a previously-destroyed wrapper instead of duplicating.
    // Posting destroy + create for the same value races the unique index and
    // raises RecordNotUnique, aborting the entire territory save inside the
    // 30-second autosave.
    const destroyedWrap = Array.from(this.hiddenFieldsTarget.children)
      .find(wrap => {
        if (norm(wrap.dataset.name) !== norm(name)) return false
        const destroyInput = wrap.querySelector('input[name$="[_destroy]"]')
        return destroyInput && destroyInput.value === 'true'
      })
    if (destroyedWrap) {
      const destroyInput = destroyedWrap.querySelector('input[name$="[_destroy]"]')
      if (destroyInput) {
        destroyInput.value = 'false'
        destroyInput.setAttribute('value', 'false')
      }
      // LRPO-5821 — remember that this wrapper came back from the dead. If a save
      // was already in flight carrying its _destroy=true, the server has destroyed
      // the record even though the flag now reads false, and the id it still holds
      // is stale. reconcileIds clears it when the response confirms the id is gone.
      // Scoped to revived wrappers ON PURPOSE: a blanket "id not returned means
      // strip it" would also fire when a card's criteria come back under a
      // different group_index, and would re-create every row as a duplicate.
      destroyedWrap.dataset.revived = 'true'
      this.appendChip(name, leadCount)
      this.committed.push(name)
      this.clearResults()
      if (this.hasInputTarget) this.inputTarget.value = ''
      this.syncCount()
      return
    }

    this.committed.push(name)
    this.appendChip(name, leadCount)
    this.appendHiddenFields(name)
    this.clearResults()
    if (this.hasInputTarget) this.inputTarget.value = ''
    this.syncCount()
  }

  // LRPO-5822 — resolve the lead count for a company committed from free text.
  //
  // ★ The SAME endpoint the dropdown uses, so the number the chip settles on is
  // the number the saved chip will show. Two implementations of one count is what
  // put this ticket back twice.
  //
  // ⚠ Matched case-insensitively via norm(): attendees.company is
  // utf8mb4_0900_ai_ci, so "AMazon" and "Amazon" are one company to the database
  // and must be one company here. A byte-exact match is how the count went
  // missing in the first place.
  //
  // ⚠ Never rejects and never throws. A chip whose count cannot be resolved stays
  // in the unknown state, which is honest; turning a failed lookup into 0 would
  // reintroduce the confident-zero bug through the back door.
  lookupLeadCount (rawName) {
    const name = String(rawName == null ? '' : rawName).trim()
    if (!name) return

    fetch(`${this.urlValue}?term=${encodeURIComponent(name)}&page=1`, {
      headers: { Accept: 'application/json' }
    })
      .then(response => (response.ok ? response.json() : null))
      .then(body => {
        if (!body) return
        const match = (body.data || []).find(row => norm(row.name) === norm(name))

        // ★ A SUCCESSFUL ANSWER WITH NO MATCH IS A REAL ZERO, not an unknown.
        // Nobody at this event carries that company name — which is exactly what
        // the free-text row promises ("we'll match them as they're scanned") — so
        // the amber zero warning is the right thing to show. Only a FAILED lookup
        // leaves the chip unknown, and that is handled by the `!body` guard above
        // and the catch below.
        this.applyLeadCount(name, match ? Number(match.lead_count) : 0)
      })
      .catch(() => {})
  }

  // Fill a rendered chip's count in after the fact. Mirrors appendChip's own
  // rules so a chip updated here is indistinguishable from one built with the
  // count already known.
  applyLeadCount (name, leadCount) {
    if (!this.hasChipsTarget) return

    const chip = Array.from(this.chipsTarget.querySelectorAll('[data-name]'))
      .find(el => norm(el.dataset.name) === norm(name))
    if (!chip) return

    let countEl = chip.querySelector('.territory-chip__count')
    if (!countEl) {
      countEl = document.createElement('span')
      countEl.className = 'territory-chip__count'
      // LRPO-5875 — same hooks as appendChip, so a chip that gains its count
      // here is as live-updatable as one that had it from the start.
      countEl.dataset.countSuffix = 'at this company'
      countEl.setAttribute('data-autosave-ignore', '')
      // Before the remove button, so the chip reads label / count / ×.
      const removeButton = chip.querySelector('.territory-chip__remove')
      if (removeButton) {
        chip.insertBefore(countEl, removeButton)
      } else {
        chip.appendChild(countEl)
      }
    }

    countEl.textContent = Number(leadCount) === 0
      ? '0 at this company'
      : `${leadCount} at this company`
    countEl.title = COUNT_TOOLTIP

    chip.classList.toggle('territory-chip--zero', Number(leadCount) === 0)
  }

  // LRPO-5821 / LRPO-5831 — find or create the hidden [_destroy] input for a
  // wrapper. A wrapper built in this session has no [_destroy] input:
  // appendHiddenFields emits only kind/value/group_index, and reconcileIds adds
  // [id] plus data-criterion-id but no destroy flag. So once a company added
  // this session had autosaved, the remove branch found nothing to flip and did
  // NOTHING — the criterion was re-submitted alive on every later save and the
  // company came back on reload.
  //
  // Re-adding it then found no wrapper marked _destroy to revive, appended a
  // SECOND wrapper with the same value, and the uniqueness validation rejected
  // the save: "Territory criteria value has already been taken", the 422 in the
  // report. The 422 was the loud half; the lost delete was the quiet one.
  //
  // Same fix and same reason as territory_demographics_controller, LRPO-5660.
  _ensureDestroyInput (wrap) {
    let destroyInput = wrap.querySelector('input[name$="[_destroy]"]')
    if (destroyInput) return destroyInput

    // ⚠ Derive the name from a SIBLING so it shares this wrapper's
    // nested-attributes index. A _destroy under a different index would
    // describe a different record and delete the wrong criterion.
    const sample = wrap.querySelector('input[name]')
    if (!sample) return null

    // ⚠ If the regex does not match, String#replace returns the name UNCHANGED —
    // which would emit a second input under the sample's own key carrying the
    // value "true". For an [id] sample that posts id="true", casts to 0, and the
    // save dies on RecordNotFound inside a blanket rescue. Bail instead: a lost
    // delete is recoverable, a mis-keyed one is not.
    const derivedName = sample.getAttribute('name').replace(/\[\w+\]$/, '[_destroy]')
    if (!derivedName.endsWith('[_destroy]')) return null

    destroyInput = document.createElement('input')
    destroyInput.type = 'hidden'
    destroyInput.setAttribute('name', derivedName)
    wrap.appendChild(destroyInput)
    return destroyInput
  }

  remove (event) {
    // ⚠ Stimulus 3 ALWAYS supplies event.params as an object, so a truthiness
    // check on it is always true and `event.params.name` is simply undefined —
    // which matched no wrapper and made every removal a silent no-op. Read the
    // key, not the container, and fall back to the button's own dataset.
    const name = (event.params && event.params.name) ||
                 (event.currentTarget || event.target).dataset.name
    this.committed = this.committedValues().filter(v => norm(v) !== norm(name))

    // Remove the matching chip.
    Array.from(this.chipsTarget.children).forEach(chip => {
      if (norm(chip.dataset.name) === norm(name)) chip.remove()
    })

    // Branch on whether the row may exist on the server. A wrapper is kept when
    // it has a criterion-id (already persisted) or was stamped by autosaving
    // (a POST has carried it and the server may have created it). Otherwise the
    // company has never been posted and cannot exist, so remove it outright.
    Array.from(this.hiddenFieldsTarget.children).forEach(wrap => {
      if (norm(wrap.dataset.name) !== norm(name)) return
      if (wrap.dataset.criterionId || wrap.dataset.posted === 'true') {
        const destroyInput = this._ensureDestroyInput(wrap)
        if (!destroyInput) return

        destroyInput.value = 'true'
        destroyInput.setAttribute('value', 'true')
      } else {
        wrap.remove()
      }
    })
    this.syncCount()

    // LRPO-5822 — REBUILD THE SUGGESTIONS. Removing a chip changes what the list
    // should say: the company is no longer "already added". Left stale, the only
    // row on screen is that note, `navigate` excludes it from `options`, and the
    // LRPO-5799 count-borrow finds nothing to borrow from — so pressing Enter
    // re-added the company reading "0 at this company" (QA's recording, 33s->34s).
    // Rebuilding also puts the real, selectable row back so the user is not
    // staring at a note about a state that has ended.
    if (this.hasInputTarget) {
      const term = this.inputTarget.value || ''
      if (term.trim().length >= 2) {
        this.performSearch(term)
      } else {
        this.clearResults()
      }
    }
  }

  // ---- rendering ----------------------------------------------------------

  appendChip (name, leadCount = null) {
    const chip = document.createElement('span')
    chip.className = 'territory-chip'
    chip.dataset.name = name

    const label = document.createElement('span')
    label.className = 'territory-chip__label'
    label.textContent = name                    // TEXT, never markup
    chip.appendChild(label)

    if (leadCount !== null) {
      const countEl = document.createElement('span')
      countEl.className = 'territory-chip__count'
      countEl.textContent = Number(leadCount) === 0 ? '0 at this company' : `${leadCount} at this company`
      countEl.title = COUNT_TOOLTIP
      // LRPO-5875 — the live writer reads the wording off the element rather
      // than carrying a copy of it, and skips the autosave for its own writes.
      // The id is not known yet: this row does not exist until the autosave
      // creates it, and reconcileIds stamps it on when the response comes back.
      countEl.dataset.countSuffix = 'at this company'
      countEl.setAttribute('data-autosave-ignore', '')
      chip.appendChild(countEl)
    }

    if (Number(leadCount) === 0 && leadCount !== null) {
      chip.classList.add('territory-chip--zero')
    }

    if (!this.readonlyValue) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'territory-chip__remove'
      button.setAttribute('aria-label', `Remove ${name}`)   // setAttribute, not innerHTML
      button.dataset.name = name
      button.dataset.action = 'territory-companies#remove'
      button.textContent = '×'
      chip.appendChild(button)
    }

    this.chipsTarget.appendChild(chip)
  }

  // The hidden inputs live in their own wrapper INSIDE hiddenFields, so removing
  // a chip cannot orphan them and removing them cannot disturb the chip list.
  appendHiddenFields (name) {
    const index = this.nextIndex()
    const wrap  = document.createElement('div')
    wrap.dataset.name = name

    wrap.appendChild(this.hiddenInput(index, 'kind', 'company'))
    wrap.appendChild(this.hiddenInput(index, 'value', name))
    wrap.appendChild(this.hiddenInput(index, 'group_index', String(this.groupValue || 0)))

    this.hiddenFieldsTarget.appendChild(wrap)
  }

  hiddenInput (index, field, value) {
    const el = document.createElement('input')
    el.type = 'hidden'
    el.setAttribute('name', `territory[territory_criteria_attributes][${index}][${field}]`)
    el.setAttribute('value', value)
    return el
  }

  // DOM-derived and STATELESS on purpose: a per-controller counter collides once
  // several group cards are on the page at the same time.
  nextIndex () {
    const existing = document.querySelectorAll(
      'input[name^="territory[territory_criteria_attributes]"]'
    )
    const indexes = Array.from(existing).map(el => {
      const m = el.getAttribute('name').match(/\[territory_criteria_attributes\]\[(\d+)\]/)
      return m ? Number(m[1]) : -1
    })
    return indexes.length ? Math.max(...indexes) + 1 : 0
  }

  renderResults (rows, term, append = false) {
    // LRPO-5655 — the "still looking" row belongs to the gap between two requests.
    // Any render at all means that gap is over, appending or not.
    this.resultsTarget.querySelectorAll('[data-role="searching"]').forEach(el => el.remove())

    if (append) {
      // Remove trailing helper rows so they stay at the end after new options.
      this.resultsTarget.querySelectorAll('[data-role="load-more"], [data-free-text]')
        .forEach(el => el.remove())
    } else {
      this.activeIndex = -1
      this.clearResults()
    }

    // LRPO-5768 — never offer a company that is already a chip. commit() rejects
    // duplicates (collation-aware) and returns SILENTLY, so such a row is a dead
    // control: it looks selectable, clicking it does nothing, and the user is
    // left thinking the app is broken. Filter with the SAME `norm` the duplicate
    // check uses, or the list and the guard disagree about what "already added"
    // means.
    const alreadyAdded = new Set(this.committedValues().map(norm))
    rows = rows.filter(row => !alreadyAdded.has(norm(row.name)))

    // LRPO-5655 — base ids on existing option count so appending does not duplicate ids.
    const idBase = append ? this.resultsTarget.querySelectorAll('li[data-name]').length : 0

    rows.forEach((row, index) => {
      const li = document.createElement('li')
      li.id = `tc-opt-${this.groupValue || 0}-${idBase + index}`
      li.setAttribute('role', 'option')
      li.textContent = `${row.name}  ·  ${row.lead_count} at this company`
      li.dataset.name = row.name
      li.dataset.leadCount = String(row.lead_count)
      li.title = COUNT_TOOLTIP
      li.dataset.action = 'click->territory-companies#pick'
      this.resultsTarget.appendChild(li)
    })

    // Free-text escape hatch: when nothing matched exactly, offer to create it.
    if (term && term.trim()) {
      const exactMatch = rows.find(row => row.name.trim().toLowerCase() === term.trim().toLowerCase())
      // Same rule for the free-text row: if the typed name is already a chip,
      // offering "Use ..." for it is the exact case QA reported.
      if (!exactMatch && !alreadyAdded.has(norm(term))) {
        const li = document.createElement('li')
        li.id = `tc-opt-${this.groupValue || 0}-${idBase + rows.length}`
        li.setAttribute('role', 'option')
        li.dataset.freeText = 'true'
        li.dataset.name = term
        li.dataset.leadCount = '0'
        li.textContent = `Use "${term}" — 0 at this company yet, we'll match them as they're scanned`
        li.dataset.action = 'click->territory-companies#pick'
        this.resultsTarget.appendChild(li)
      }
    }

    // LRPO-5798 — if nothing rendered and the term is already committed,
    // show a note so the user knows the company exists.
    if (!this.resultsTarget.children.length && term && term.trim() && alreadyAdded.has(norm(term))) {
      const li = document.createElement('li')
      li.dataset.role = 'already-added'
      li.setAttribute('aria-disabled', 'true')
      li.textContent = `"${term.trim()}" is already added to this territory`
      this.resultsTarget.appendChild(li)
    }

    // LRPO-5655 — paging restored at Mark's direction. The row is a BUTTON, not
    // an option: `navigate` must never let the arrow keys land on it, or Enter
    // would commit a company literally named "Show more".
    if (this.hasMore) {
      const more = document.createElement('li')
      more.dataset.role = 'load-more'
      more.setAttribute('role', 'presentation')
      more.textContent = 'Show more'
      more.dataset.action = 'click->territory-companies#loadMore'
      this.resultsTarget.appendChild(more)
    }
  }

  pick (event) {
    const el = event.currentTarget
    this.commit(el.dataset.name, Number(el.dataset.leadCount))
  }

  // LRPO-5655 — fetch the next page of suggestions and append them.
  loadMore () {
    const term = this.inputTarget.value
    if (!term || term.trim().length < 2) return

    this.page = (this.page || 1) + 1

    fetch(`${this.urlValue}?term=${encodeURIComponent(term)}&page=${this.page}`,
        { headers: { Accept: 'application/json' } })
      .then(response => response.json())
      .then(body => {
        this.hasMore = !!body.has_more
        this.renderResults(body.data || [], term, true)
        this.autoAdvanceIfEmpty(term)
      })
      .catch(() => { this.hasMore = false })
  }

  // LRPO-5655 (QA return) — page one can be entirely filtered out.
  //
  // ★ THE FILTER IS RIGHT, THE STOPPING RULE WAS WRONG. renderResults drops every
  // company that is already a chip (LRPO-5768 — offering one is a dead control).
  // Once all ten results on a page have been picked, that leaves an empty list for
  // a prefix with dozens of live matches behind it. Treat "nothing visible" as
  // "not finished", not as "no matches".
  //
  // ⚠ THE FREE-TEXT ROW DOES NOT COUNT. Every search ends with a "Use <term>" row
  // that also carries data-name, so counting bare [data-name] would see one row and
  // conclude the list is fine.
  //
  // ⚠ BOUNDED, AND ONLY WHILE THE TERM STILL STANDS. The user may have typed on
  // while these requests were in flight; advancing then would append results for a
  // word that is no longer in the box.
  autoAdvanceIfEmpty (term) {
    if (!this.hasResultsTarget || !this.hasInputTarget) return
    if (!this.hasMore) return
    if ((this.autoAdvances || 0) >= MAX_AUTO_ADVANCE) return
    if (this.inputTarget.value.trim() !== String(term || '').trim()) return

    const selectable = this.resultsTarget
      .querySelectorAll('li[data-name]:not([data-free-text])').length
    if (selectable > 0) return

    this.autoAdvances = (this.autoAdvances || 0) + 1
    this._showSearching()
    this.loadMore()
  }

  // LRPO-5655 — what to show in the gap between "page 1 was all already-added" and
  // "page 2 arrived".
  //
  // ★ Without this the user is shown the dead end this ticket was reopened for —
  // the already-added note plus a Show more button — and then it is replaced a
  // moment later. Being told "nothing here, click this" and then contradicted is
  // worse than being told to wait.
  //
  // ⚠ role="presentation" and data-role="searching": `navigate` excludes this row,
  // because a plain <li> here is arrowable and Enter would commit a company
  // literally named "Searching…" — the same trap the Show more row needed.
  // ⚠ textContent, never innerHTML.
  _showSearching () {
    if (!this.hasResultsTarget) return

    this.clearResults()

    const li = document.createElement('li')
    li.dataset.role = 'searching'
    li.setAttribute('role', 'presentation')
    li.setAttribute('aria-live', 'polite')
    li.textContent = 'Searching for more companies…'
    this.resultsTarget.appendChild(li)
  }

  clearResults () {
    if (!this.hasResultsTarget) return
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }
    this.activeIndex = -1
    if (this.hasInputTarget) this.inputTarget.removeAttribute('aria-activedescendant')
  }

  committedValues () {
    return this.committed || []
  }

  // LRPO-5800 — the counter and "not set" badge disagreed: the badge was server-rendered inside
  // `<% if criteria.empty? %>` with no Stimulus target, so nothing on the client could hide it.
  // Add a company and the header reads "Companies 1" directly above a badge insisting nothing is
  // set. Now the badge tracks the count via the `unset` target so the two always agree.
  // The badge is NOT hidden merely because the count is zero — its text ("Leads from any company
  // can match this territory") explains matching behaviour that a bare "0" does not convey. A bare
  // 0 reads like "nothing matches", which is the opposite of the truth.
  syncCount () {
    if (this.hasCountTarget) this.countTarget.textContent = String(this.committedValues().length)
    if (this.hasUnsetTarget) this.unsetTarget.hidden = this.committedValues().length > 0
    this.refreshOperators()
  }

  // LRPO-5814 — companies are always an OR, so every chip after the first
  // carries the operator. Recomputed from DOM order rather than appended, so
  // removing the first company correctly promotes the second instead of
  // leaving it with a leading OR.
  refreshOperators () {
    if (!this.hasChipsTarget) return

    const chips = [...this.chipsTarget.querySelectorAll('.territory-chip')]

    chips.forEach((chip, index) => {
      const existing = chip.querySelector('[data-role="operator"]')

      // First chip must never have an operator — remove only if present.
      if (index === 0) {
        if (existing) existing.remove()
        return
      }

      // LRPO-5826 — idempotence: leave the operator alone when it's already correct,
      // so reconcileIds doesn't churn the DOM and trigger another autosave.
      if (existing && existing.textContent.trim().toUpperCase() === 'OR') return

      if (existing) existing.remove()

      const op = document.createElement('span')
      op.className = 'territory-chip__operator'
      op.dataset.role = 'operator'
      op.textContent = 'OR'
      chip.prepend(op)
    })
  }

  // ---- id reconciliation --------------------------------------------------

  reconcileIds (detail) {
    if (!detail || !Array.isArray(detail.territory_criteria)) return

    const ourGroup = Number(this.groupValue || 0)
    const ours = detail.territory_criteria.filter(c => {
      return c.kind === 'company' && Number(c.group_index) === ourGroup
    })

    if (!this.hasHiddenFieldsTarget) return

    // Collect every id already claimed by a wrapper in the document so we never
    // hand out one that belongs to another card.
    const claimed = new Set(
      Array.from(document.querySelectorAll('input[name$="[id]"]'))
        .map(el => el.value)
    )

    // 1. Assign server-returned ids to new wrappers (those missing [id]).
    Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
      const idInput = wrapper.querySelector('input[name$="[id]"]')
      if (idInput) return                                   // already has an id

      const match = ours.find(server =>
        norm(server.value) === norm(wrapper.dataset.name) &&
        !claimed.has(String(server.id))
      )
      if (!match) return

      claimed.add(String(match.id))
      wrapper.dataset.criterionId = String(match.id)

      // LRPO-5875 — the id we just claimed is the only thing standing between
      // this chip and the live count writer, which queries the document for
      // [data-criterion-id]. The wrapper is in hiddenFields; the chip is a
      // separate element in chipsTarget, matched by name like everywhere else
      // in this controller.
      //
      // ⚠ Stamp the COUNT SPAN, never the chip. The live writer sets
      // textContent on whatever it matches, so an id on the chip itself would
      // erase the company name.
      if (this.hasChipsTarget) {
        const chip = Array.from(this.chipsTarget.querySelectorAll('[data-name]'))
          .find(el => norm(el.dataset.name) === norm(wrapper.dataset.name))
        const chipCount = chip && chip.querySelector('.territory-chip__count')
        if (chipCount) chipCount.dataset.criterionId = String(match.id)
      }

      // Derive the [id] name from any sibling: same prefix, trailing key → "id".
      const sample = wrapper.querySelector('input')
      if (sample) {
        const idName = sample.getAttribute('name').replace(/\[\w+\]$/, '[id]')
        const input = document.createElement('input')
        input.type = 'hidden'
        input.setAttribute('name', idName)
        input.setAttribute('value', String(match.id))
        wrapper.prepend(input)
      }
    })

    // 2. Remove wrappers whose destroy has completed (id no longer returned).
    const returnedIds = new Set(ours.map(c => String(c.id)))
    Array.from(this.hiddenFieldsTarget.children).forEach(wrapper => {
      if (!wrapper.dataset.criterionId) return

      const stillLive = returnedIds.has(wrapper.dataset.criterionId)
      const destroyInput = wrapper.querySelector('input[name$="[_destroy]"]')

      if (destroyInput && destroyInput.value === 'true' && !stillLive) {
        wrapper.remove()
        return
      }

      // LRPO-5821 — a REVIVED wrapper whose id the server no longer returns. The
      // save that was in flight when the user re-added carried _destroy=true, so
      // the record really was destroyed even though the flag now reads false.
      // Posting that id again dies on RecordNotFound and takes the whole territory
      // save with it. Strip it and let the row post as the create it now is.
      //
      // ⚠ Deliberately keyed on data-revived, NOT on "the id was not returned".
      // `ours` is filtered by group_index, so the broader rule would strip every
      // id on a card whose criteria came back under a different index and
      // duplicate the lot.
      if (wrapper.dataset.revived === 'true' && !stillLive) {
        const idInput = wrapper.querySelector('input[name$="[id]"]')
        if (idInput) idInput.remove()
        delete wrapper.dataset.criterionId
      }

      if (stillLive) delete wrapper.dataset.revived
    })
    this.refreshOperators()
  }
}
